/**
 * InfoCard Component
 *
 * A reusable card component for documentation and informational content.
 * Supports multiple variants for different visual emphasis.
 */

import { clsx } from 'clsx'

export type InfoCardVariant = 'default' | 'info' | 'warning'

const variantStyles: Record<
  InfoCardVariant,
  { container: string; title: string }
> = {
  default: {
    container: 'bg-black/20 border-white/5 hover:border-white/10',
    title: 'text-primary',
  },
  info: {
    container: 'bg-blue-900/10 border-blue-500/20 hover:border-blue-500/40',
    title: 'text-blue-400',
  },
  warning: {
    container: 'bg-yellow-900/10 border-yellow-500/20 hover:border-yellow-500/40',
    title: 'text-yellow-400',
  },
}

interface InfoCardProps {
  title: string
  children: React.ReactNode
  variant?: InfoCardVariant
}

export function InfoCard({
  title,
  children,
  variant = 'default',
}: InfoCardProps) {
  const styles = variantStyles[variant]

  return (
    <div
      className={clsx(
        'p-5 rounded-xl border glass-panel transition-colors',
        styles.container
      )}
    >
      <h3
        className={clsx(
          'font-bold mb-3 uppercase tracking-wider text-sm',
          styles.title
        )}
      >
        {title}
      </h3>
      <div className="text-white text-sm leading-relaxed">{children}</div>
    </div>
  )
}
